import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ScheduledMeeting } from '../../../contexts';
import { formatMeetingToDto } from '../helpers/formatters';
import type { SelectedMeetingState } from '../types';

const useSelectedMeeting = (
  meetings: ScheduledMeeting[],
  defaultMeetingId: string | null,
): SelectedMeetingState => {
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(() =>
    meetings.length > 0 ? meetings[0].room : null,
  );

  const selectedMeeting = useMemo(() => {
    if (!meetings.length) {
      return null;
    }

    const updatedMeeting = selectedRoomId
      ? meetings.find(({ room }) => room === selectedRoomId)
      : null;

    return updatedMeeting ?? null;
  }, [meetings, selectedRoomId]);

  useEffect(() => {
    if (selectedMeeting && selectedMeeting.room !== selectedRoomId) {
      setSelectedRoomId(selectedMeeting.room);
    }
  }, [selectedMeeting, selectedRoomId]);

  const meetingData = useMemo(
    () => (selectedMeeting ? formatMeetingToDto(selectedMeeting, defaultMeetingId) : null),
    [selectedMeeting, defaultMeetingId],
  );

  const selectMeetingHandler = useCallback((room: string | null) => {
    if (!room) {
      return;
    }

    setSelectedRoomId(room);
  }, []);

  return {
    selectedMeeting,
    meetingData,
    selectMeetingHandler,
  };
};

export default useSelectedMeeting;
